/**
 * ALBERO quick add (product cards) and "shop the look".
 *
 * - Single-variant products are added straight to the cart.
 * - Products with options open the quick-add sheet; its content is the `albero-quick-add`
 *   section rendered by Shopify (Section Rendering API), so price, options and availability
 *   always come from Liquid.
 * - "Shop the look" walks through every product that needs a choice and then adds all the
 *   chosen variants to the cart in a single request.
 */
import { AlberoDialog, addToCart, config, formatString, announce } from '@albero/core';

const SECTION_ID = 'albero-quick-add';

class AlberoQuickAdd extends AlberoDialog {
  #controller = null;
  #sequence = null;
  #beforeAdd = (event) => this.#onBeforeAdd(event);
  #afterAdd = () => {
    if (!this.#sequence) this.close({ restoreFocus: false });
  };

  connectedCallback() {
    super.connectedCallback();
    this.body = this.querySelector('[data-qa-body]');
    this.loadingMarkup = this.body?.innerHTML || '';
    this.addEventListener('albero:before-add', this.#beforeAdd);
    this.addEventListener('albero:product-added', this.#afterAdd);
    this.addEventListener('albero:dialog-close', () => {
      this.#sequence = null;
      this.#controller?.abort();
    });
  }

  async openFor(url, trigger) {
    this.#sequence = null;
    this.open(trigger);
    await this.#load(url);
  }

  /**
   * @param {Array<{url: string}>} steps products that need a choice
   * @param {Array<{id: string, quantity: number}>} ready variants already resolved (single-variant products)
   * @param {Element} trigger
   * @param {Element} errorTarget
   */
  async startSequence(steps, ready, trigger, errorTarget) {
    this.#sequence = { steps, ready: [...ready], chosen: [], index: 0, errorTarget };
    this.open(trigger);
    await this.#loadStep();
  }

  async #loadStep() {
    const sequence = this.#sequence;
    if (!sequence) return;
    const step = sequence.steps[sequence.index];
    await this.#load(step.url);
    if (this.#sequence !== sequence) return;
    const label = this.body.querySelector('[data-qa-step]');
    if (label) {
      label.textContent = formatString(config.strings.lookStep, {
        current: sequence.index + 1,
        total: sequence.steps.length,
      });
    }
    const isLast = sequence.index === sequence.steps.length - 1;
    if (!isLast) {
      const addLabel = this.body.querySelector('[data-add-label]');
      const template = this.body.querySelector('template[data-add-label-default]');
      if (addLabel) addLabel.textContent = config.strings.nextPiece;
      if (template) template.innerHTML = config.strings.nextPiece;
      this.body.querySelectorAll('[data-add-price], .albero-product-form__dash').forEach((el) => (el.hidden = true));
    }
  }

  async #load(url) {
    if (!this.body) return;
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.body.innerHTML = this.loadingMarkup;
    this.setAttribute('aria-busy', 'true');
    try {
      const separator = url.includes('?') ? '&' : '?';
      const response = await fetch(`${url}${separator}section_id=${SECTION_ID}`, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('.albero-qa');
      if (!content) throw new Error('missing content');
      this.body.replaceChildren(document.importNode(content, true));
      const firstInput = this.body.querySelector('[data-option-value]') || this.body.querySelector('[data-add-button]');
      firstInput?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.body.innerHTML = `<p class="albero-form-error" role="alert">${config.strings.addError}</p>`;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /** In a look sequence, collect the chosen variant instead of adding it immediately. */
  async #onBeforeAdd(event) {
    const sequence = this.#sequence;
    if (!sequence) return;
    event.preventDefault();
    const { formData } = event.detail;
    sequence.chosen.push({ id: formData.get('id'), quantity: Number(formData.get('quantity') || 1) });
    sequence.index += 1;

    if (sequence.index < sequence.steps.length) {
      await this.#loadStep();
      return;
    }

    // Last piece chosen: add the whole look in one request.
    const items = [...sequence.ready, ...sequence.chosen];
    const product = event.detail.product;
    const button = product?.querySelector('[data-add-button]');
    button?.classList.add('is-loading');
    try {
      await addToCart(items, { source: this, context: 'dialog' });
      this.#sequence = null;
      this.close({ restoreFocus: false });
    } catch (error) {
      const message = product?.querySelector('[data-form-error]');
      if (message) {
        message.textContent = error.message || config.strings.addError;
        message.hidden = false;
      }
      announce(error.message || config.strings.addError);
    } finally {
      button?.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('albero-quick-add')) customElements.define('albero-quick-add', AlberoQuickAdd);

/* -------------------------------------------------------------------------- */
/* Card triggers                                                               */
/* -------------------------------------------------------------------------- */

document.addEventListener('click', async (event) => {
  const quick = event.target.closest('[data-albero-quick-add]');
  if (quick) {
    event.preventDefault();
    const dialog = document.getElementById('AlberoQuickAdd');
    if (dialog?.openFor) dialog.openFor(quick.dataset.alberoQuickAdd, quick);
    else window.location.href = quick.dataset.alberoQuickAdd;
    return;
  }

  const direct = event.target.closest('[data-albero-add-variant]');
  if (direct) {
    event.preventDefault();
    if (direct.classList.contains('is-loading')) return;
    direct.classList.add('is-loading');
    direct.setAttribute('aria-busy', 'true');
    try {
      await addToCart([{ id: direct.dataset.alberoAddVariant, quantity: 1 }], { source: direct });
    } catch (error) {
      announce(error.message || config.strings.addError);
      direct.classList.add('has-error');
      window.setTimeout(() => direct.classList.remove('has-error'), 2400);
    } finally {
      direct.classList.remove('is-loading');
      direct.removeAttribute('aria-busy');
    }
  }
});

/* -------------------------------------------------------------------------- */
/* Shop the look                                                               */
/* -------------------------------------------------------------------------- */

class AlberoLook extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-look-add]');
    this.error = this.querySelector('[data-look-error]');
    this.button?.addEventListener('click', () => this.#start());
  }

  async #start() {
    if (this.button.classList.contains('is-loading')) return;
    if (this.error) this.error.hidden = true;
    const items = [...this.querySelectorAll('[data-look-item]')].filter((item) => item.dataset.available === 'true');
    const ready = items.filter((item) => item.dataset.variantId).map((item) => ({ id: item.dataset.variantId, quantity: 1 }));
    const steps = items.filter((item) => !item.dataset.variantId).map((item) => ({ url: item.dataset.url }));

    if (!steps.length) {
      this.button.classList.add('is-loading');
      try {
        await addToCart(ready, { source: this });
      } catch (error) {
        if (this.error) {
          this.error.textContent = error.message || config.strings.addError;
          this.error.hidden = false;
        }
      } finally {
        this.button.classList.remove('is-loading');
      }
      return;
    }

    const dialog = document.getElementById('AlberoQuickAdd');
    if (dialog?.startSequence) dialog.startSequence(steps, ready, this.button, this.error);
  }
}

if (!customElements.get('albero-look')) customElements.define('albero-look', AlberoLook);